import { CartViewEvent, CartDiscountUpdateEvent, CartErrorEvent } from '@theme/standard-events';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');

    this.listeners = {
      open: event => this.open(event),
      toggle: (event) => {
        if (event.newState === 'open') this.dispatchCartView();
      },
    };

    this.addEventListener('cart-drawer:open', this.listeners.open);

    // The toggle event fires for every open path: the header icon (native invoker
    // button), the cart-drawer:open event, and Shopify.actions.openCart
    this.dialog?.addEventListener('toggle', this.listeners.toggle);
  }

  disconnectedCallback() {
    this.removeEventListener('cart-drawer:open', this.listeners.open);
    this.dialog?.removeEventListener('toggle', this.listeners.toggle);
  }

  open(event) {
    if (!this.dialog) return;

    const trigger = event?.detail?.trigger || null;

    // The add-to-cart flow already has a cart request in flight; reuse its promise for
    // cart:view instead of fetching the cart a second time
    this.pendingCartPromise = event?.detail?.cartPromise || null;

    this.dialog.showModal(trigger);
  }

  // Shopify standard event: cart is viewed in the drawer. Fetched via the AJAX API and
  // converted, because the Shopify.actions.getCart() cart shape does not pass the
  // standard-events payload validation.
  dispatchCartView() {
    const cartPromise = this.pendingCartPromise
      ?? fetch(`${routes.cart_url}.js`)
        .then(response => response.json())
        .then(ajaxCart => ({ cart: CartViewEvent.createCartFromAjaxResponse(ajaxCart) }));
    this.pendingCartPromise = null;

    cartPromise
      .then(({ cart }) => {
        this.dispatchEvent(
          new CartViewEvent({
            context: 'dialog',
            cart,
          }),
        );
      })
      .catch(() => {});
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDiscountComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
        existingDiscounts: new Set(dataOptions.existingDiscounts ?? []),
      };
    }

    this.cartDiscountError = this.querySelector('[ref="cartDiscountError"]');
    this.cartDiscountErrorDiscountCode = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
    this.cartDiscountErrorShipping = this.querySelector('[ref="cartDiscountErrorShipping"]');

    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.updateDiscounts);

    // Delegated: the renderer replaces the applied-discount buttons after every cart
    // mutation, so per-button listeners would go stale
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-discount-remove]')) this.updateDiscounts(event);
    });
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.updateDiscounts);
  }

  updateDiscounts = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const existingDiscounts = this.options.existingDiscounts;
    const removeTrigger = event.target.closest('[data-discount-remove]');
    const type = removeTrigger ? 'discount_remove' : 'discount_add';
    const discountCodeValue = type === 'discount_add' ? this.form.querySelector('input[name="discount"]').value : removeTrigger.dataset.discountRemove;

    if (type === 'discount_remove') {
      if (!existingDiscounts.has(discountCodeValue.toLowerCase())) return;

      existingDiscounts.delete(discountCodeValue.toLowerCase());
    }
    else if (type === 'discount_add' && existingDiscounts.has(discountCodeValue.toLowerCase())) return;
    else if (type === 'discount_add') {
      this.form.querySelector('[type="submit"]').classList.add('button--loading');
      this.form.querySelector('[type="submit"]').setAttribute('disabled', true);
    }

    // Shopify standard event, dispatched before the fetch; the cart section renderer
    // re-renders the [data-cart-section="discounts"] marker when it settles
    let deferred = null;

    try {
      const discounts = type === 'discount_add' ? [...existingDiscounts, discountCodeValue] : [...existingDiscounts];

      deferred = CartDiscountUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: discounts.map(code => ({ code })),
          promise: deferred.promise,
        }),
      );

      this.cartDiscountError.classList.add('hidden');
      this.cartDiscountErrorDiscountCode.classList.replace('flex', 'hidden');
      this.cartDiscountErrorShipping.classList.replace('flex', 'hidden');

      const body = JSON.stringify({
        discount: discounts.join(','),
      });

      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        ...{ body },
      });

      const data = await response.json();

      deferred.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      // if the discount is not applicable, show an error text
      if (data.discount_codes.find(discount => discount.code === discountCodeValue && discount.applicable === false)) {
        this.#handleDiscountError('discount_code');
        return;
      }

      // Wait for the renderer to swap the applied-discounts marker, then check the live DOM:
      // if the code was accepted but no discount button was added, it's a shipping discount
      this.#afterCartSectionsRendered(() => {
        const renderedCodes = [...this.querySelectorAll('[data-cart-section="discounts"] [data-discount-remove]')]
          .map(element => element.dataset.discountRemove)
          .filter(Boolean);

        if (type !== 'discount_add') return;

        const isApplicable = data.discount_codes.find(discount => discount.code === discountCodeValue && discount.applicable === true);
        const isShippingDiscount =
          isApplicable &&
          renderedCodes.length === existingDiscounts.size &&
          renderedCodes.every(code => existingDiscounts.has(code));

        if (isShippingDiscount) {
          this.#handleDiscountError('shipping');
          return;
        }

        if (isApplicable) {
          // The component itself is not re-rendered, so track the new code in memory
          existingDiscounts.add(discountCodeValue.toLowerCase());
          this.form.querySelector('input[name="discount"]').value = '';
        }
      });
    }
    catch (error) {
      console.error(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: window.cartStrings.error,
          code: 'SERVICE_UNAVAILABLE',
        }),
      );
      deferred?.reject(error);
    }
    finally {
      if (type === 'discount_add') {
        this.form.querySelector('[type="submit"]').classList.remove('button--loading');
        this.form.querySelector('[type="submit"]').removeAttribute('disabled');
      }
    }
  };

  #afterCartSectionsRendered(callback) {
    let handled = false;
    const handler = () => {
      handled = true;
      callback();
    };

    document.addEventListener('cart-sections:rendered', handler, { once: true });
    setTimeout(() => {
      if (!handled) {
        document.removeEventListener('cart-sections:rendered', handler);
        callback();
      }
    }, 3000);
  }

  #handleDiscountError(type) {
    const target = type === 'discount_code' ? this.cartDiscountErrorDiscountCode : this.cartDiscountErrorShipping;
    this.cartDiscountError.classList.remove('hidden');
    target.classList.replace('hidden', 'flex');
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscountComponent);
}
